import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import CustomSearchBar from "../Lease Module/CustomSearchBar";
import ReUsableCard from '../Lease Module/ReUsableCard';
import CustomNav from "../Buy Module/CustomNav";
import { BASE_URL } from "../../Api/ApiUrls";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import buildingImage from './../../../Images/house.jpeg';
import usePropertyActions from './UseBuyerServices';
import { useContext } from "react";
import { AuthContext } from "../../AuthContext/AuthContext";
import GuestLoginModal from '../../GuestLoginModal';

// Fix Leaflet default icon issue
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

// BANK AUCTION API 

// Property type filter options
const rentalTypes = [
  "PLOT/LAND", "COMMERCIAL LAND/PLOT", "RENT WITH DUPLEX BUILDING", "DUPLEX HOUSE",
  "RENTAL BUILDING", "PG-OFFICES", "FLAT", "VILLA",
  "COMMERCIAL BUILDING", "APARTMENT", "JV/JD"
];

const PostBankMap = () => {
  const navigate = useNavigate();
  const { isSubscribed, savedProperties, saving, toggleSave, isSaved } = usePropertyActions();
  const [likedCards, setLikedCards] = useState({});
  const { userId, isGuest } = useContext(AuthContext);
  const [showGuestModal, setShowGuestModal] = useState(false);

  // --- STATE ---
  const [properties, setProperties] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [visibleProperties, setVisibleProperties] = useState([]);
  const [activePropertyId, setActivePropertyId] = useState(null);
  const [searchLocation, setSearchLocation] = useState(null);
  const [priceFilter, setPriceFilter] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedPropertyType, setSelectedPropertyType] = useState(null);
  // State for carousel arrows
  const [isAtStart, setIsAtStart] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(false);

  const mapContainerRef = useRef(null);
  const cardListRef = useRef(null);

  const [map, setMap] = useState(null);
  const [markers, setMarkers] = useState([]);
  const [mapLoaded, setMapLoaded] = useState(false);

  const [currentBounds, setCurrentBounds] = useState(null);
  const [mapKey, setMapKey] = useState(0);

  // Refs for controlling behavior
  const skipMapPanRef = useRef(false);
  const isProgrammaticScrollRef = useRef(false);
  const scrollTimeoutRef = useRef(null);
  const scrollDebounceRef = useRef(null);
  const lastActiveIdRef = useRef(null);
  const isPointerOverCarouselRef = useRef(false);

  const searchLocationMarkerRef = useRef(null);

  // Disable map interactions when interacting with carousel
  const disableMapInteractions = () => {
    if (!map) return;
    try {
      if (map.dragging && map.dragging.enabled()) map.dragging.disable();
      if (map.touchZoom && map.touchZoom.enabled()) map.touchZoom.disable();
      if (map.doubleClickZoom && map.doubleClickZoom.enabled()) map.doubleClickZoom.disable();
      if (map.scrollWheelZoom && map.scrollWheelZoom.enabled()) map.scrollWheelZoom.disable();
    } catch (err) { /* ignore */ }
  };

  const enableMapInteractions = () => {
    if (!map) return;
    try {
      if (map.dragging && !map.dragging.enabled()) map.dragging.enable();
      if (map.touchZoom && !map.touchZoom.enabled()) map.touchZoom.enable();
      if (map.doubleClickZoom && !map.doubleClickZoom.enabled()) map.doubleClickZoom.enable();
      if (map.scrollWheelZoom && !map.scrollWheelZoom.enabled()) map.scrollWheelZoom.enable();
    } catch (err) { /* ignore */ }
  };

  const requireLogin = (e, callback) => {
    e.stopPropagation();

    if (isGuest) {
      setShowGuestModal(true);
      return;
    }

    callback();
  };

  // Handle carousel pointer events to disable map interactions
  useEffect(() => {
    const container = cardListRef.current;
    if (!container) return;

    const onPointerDown = (e) => {
      isPointerOverCarouselRef.current = true;
      disableMapInteractions();
    };
    
    const onPointerUp = (e) => {
      isPointerOverCarouselRef.current = false;
      setTimeout(() => enableMapInteractions(), 40);
    };
    
    const onPointerCancel = () => {
      isPointerOverCarouselRef.current = false;
      setTimeout(() => enableMapInteractions(), 40);
    };

    container.addEventListener('pointerdown', onPointerDown, { passive: true });
    window.addEventListener('pointerup', onPointerUp, { passive: true });
    container.addEventListener('pointercancel', onPointerCancel, { passive: true });
    container.addEventListener('touchcancel', onPointerCancel, { passive: true });

    return () => {
      container.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointerup', onPointerUp);
      container.removeEventListener('pointercancel', onPointerCancel);
      container.removeEventListener('touchcancel', onPointerCancel);
    };
  }, [map, cardListRef.current]);

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const categoriesResponse = await axios.get(`${BASE_URL}/property-category/`);
        setCategories(categoriesResponse.data);

        const propertiesResponse = await axios.get(`${BASE_URL}/property/`);
        const filtered = propertiesResponse.data.filter(item =>
          item.status === true && item.type && item.type.toLowerCase().includes("sell")
        );

        const parsed = filtered.map(item => {
          const parseCoord = (coord) => {
            if (!coord) return null;
            const cleaned = coord.replace(/"/g, '').replace(':', '.');
            return parseFloat(cleaned);
          };

          const matchedCategory = categoriesResponse.data.find(
            cat => cat.category_id == item.category_id
          );

          // Parse lat/long from API - convert string to number
          const categoryName = matchedCategory ? matchedCategory.category : 'Property';

          const images = item.property_images?.length > 0
            ? item.property_images.map(img => `${BASE_URL}${img.image}`)
            : [buildingImage];

          return {
            // FIX: Ensure ID is always string
            id: String(item.property_id),
            title: categoryName,
            location: item.location || 'Not specified',
            price: Number(item.price) || 0,
            date: item.created_at?.split('T')[0],
            facing: item.facing?.replace(/"/g, '') || 'N/A',
            area: item.site_area ? `${item.site_area} sq ft` : 'N/A',
            dimensions: item.roadwidth ? `${item.roadwidth} ft road` : 'N/A',
            listedBy: item.posted_by?.replace(/"/g, '') || 'Agent',
            lat: parseCoord(item.lat),
            lng: parseCoord(item.long),
            length: item.length,
            width: item.width,
            property_name: item.property_name,
            mobile_no: item.mobile_no,
            images: images,
            propertyData: item,
            owner: item.user_id,
            createdAt: item.created_at
          };
        });

        setProperties(parsed);
      } catch (error) {
        console.error('Error fetching properties:', error);
      }
    };

    fetchProperties();
  }, []);

  // FIX: Add this useEffect to sync active property with visible properties
  useEffect(() => {
    if (visibleProperties.length > 0) {
      // If no active property or active property not in visible ones
      if (!activePropertyId || !visibleProperties.find(p => String(p.id) === String(activePropertyId))) {
        const firstId = String(visibleProperties[0].id);
        setActivePropertyId(firstId);
        
        // Scroll to it after a small delay
        setTimeout(() => {
          scrollToCard(firstId);
        }, 100);
      }
    }
  }, [visibleProperties]);

  const toggleLike = (id) => {
    setLikedCards((prev) => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  useEffect(() => {
    if (properties.length === 0) return;

    let filtered = properties.filter(p => p.lat !== null && p.lng !== null);

    // ✅ FIXED: Property type filtering
    if (selectedPropertyType) {
      const searchType = selectedPropertyType.trim().toUpperCase();
      
      filtered = filtered.filter(property => {
        // Check multiple possible sources for property type
        const propType = (
          property.propertyData?.property_type || 
          property.title || 
          property.propertyData?.category ||
          ""
        )
          .toString()
          .trim()
          .toUpperCase();

        if (!propType) return false;

        // More flexible matching
        return propType === searchType || 
               propType.includes(searchType) || 
               searchType.includes(propType);
      });
    }

    // Apply price filter
    if (priceFilter) {
      filtered = filtered.filter(property => {
        const price = property.price || 0;
        const numericPrice = Number(price);
        
        // Handle "No Min" filter (min and max are both 0)
        if (priceFilter.min === 0 && priceFilter.max === 0) {
          return true;
        }
        
        // Handle maximum value (Infinity case)
        if (priceFilter.max === Infinity) {
          return numericPrice >= priceFilter.min;
        }
        
        return numericPrice >= priceFilter.min && numericPrice <= priceFilter.max;
      });
    }

    // Sort by distance if search location is selected
    if (searchLocation) {
      const centerLat = searchLocation.lat;
      const centerLng = searchLocation.lng;
      
      filtered = filtered
        .map(prop => {
          if (!prop.lat || !prop.lng) return prop;
          const distance = L.latLng([prop.lat, prop.lng]).distanceTo([centerLat, centerLng]);
          return { ...prop, distance };
        })
        .sort((a, b) => (a.distance || Infinity) - (b.distance || Infinity));
    }

    setFilteredProperties(filtered);

    // Reset active property and scroll when filters change
    if (cardListRef.current) {
      cardListRef.current.scrollLeft = 0;
    }
    setActivePropertyId(null);

  }, [properties, selectedPropertyType, priceFilter, searchLocation]);

  // Initialize map
  useEffect(() => {
    if (!mapContainerRef.current) return;

    let initialCenter = [17.385, 78.486];
    let initialZoom = 13;

    if (searchLocation) {
      initialCenter = [searchLocation.lat, searchLocation.lng];
      initialZoom = 13;
    }

    const leafletMap = L.map(mapContainerRef.current, {
      center: initialCenter,
      zoom: initialZoom,
      zoomControl: false,
      maxZoom: 19, // Increased from 13 to allow more zooming
    });

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; OpenStreetMap contributors',
      maxNativeZoom: 18
    }).addTo(leafletMap);

    L.control.zoom({ position: "topright" }).addTo(leafletMap);

    // Handle map move
    const handleMoveEnd = () => {
      setCurrentBounds(leafletMap.getBounds());
    };

    const handleZoomEnd = () => {
      const currentZoom = leafletMap.getZoom();
      const bounds = leafletMap.getBounds();
      setCurrentBounds(bounds);
      
      // Update marker icons with price labels based on zoom level
      setTimeout(() => {
        setMarkers(prevMarkers => {
          prevMarkers.forEach(m => {
            const pid = m.options.propertyId;
            const prop = filteredProperties.find(p => p.id === pid);
            if (prop && leafletMap.hasLayer(m)) {
              m.setIcon(createMarkerIconForProperty(prop, pid === activePropertyId));
            }
          });
          return [...prevMarkers];
        });
      }, 50);
    };

    leafletMap.on("moveend", handleMoveEnd);
    leafletMap.on("zoomend", handleZoomEnd);

    setMap(leafletMap);
    setMapLoaded(true);
    setCurrentBounds(leafletMap.getBounds());

    return () => {
      if (leafletMap) {
        leafletMap.off("moveend", handleMoveEnd);
        leafletMap.off("zoomend", handleZoomEnd);
        
        // Remove all layers first
        leafletMap.eachLayer(layer => {
          try {
            leafletMap.removeLayer(layer);
          } catch (e) {}
        });
        
        leafletMap.remove();
      }
      
      setMap(null);
      setMapLoaded(false);
      setMarkers([]);
    };
  }, [mapKey, searchLocation]);

  // Update map when search location changes
  useEffect(() => {
    if (map && searchLocation) {
      try {
        map.setView([searchLocation.lat, searchLocation.lng], 13, {
          animate: true
        });
        
        // Remove existing search marker
        if (searchLocationMarkerRef.current && map.hasLayer(searchLocationMarkerRef.current)) {
          map.removeLayer(searchLocationMarkerRef.current);
        }
        
        // Add new search marker
        const searchIcon = L.divIcon({
          className: 'search-location-marker',
          html: '',
          iconSize: [12, 12],
          iconAnchor: [6, 6],
        });
        
        searchLocationMarkerRef.current = L.marker([searchLocation.lat, searchLocation.lng], {
          icon: searchIcon
        }).addTo(map);
          
      } catch (error) {
        console.error('Error updating map to search location:', error);
      }
    }
  }, [searchLocation, map]);

  // Update visible properties when bounds change
  useEffect(() => {
    if (!currentBounds || filteredProperties.length === 0) {
      setVisibleProperties(filteredProperties);
      return;
    }

    const inBounds = filteredProperties.filter((p) => {
      try {
        return p.lat && p.lng && currentBounds.contains(L.latLng(p.lat, p.lng));
      } catch (error) {
        return false;
      }
    });

    setVisibleProperties(inBounds);
    
    // If active property is no longer in view, clear it
    if (activePropertyId && !inBounds.find(p => String(p.id) === String(activePropertyId))) {
      setActivePropertyId(null);
    }
  }, [currentBounds, filteredProperties, activePropertyId]);

  // Price label formatting
  const getPriceLabel = (price) => {
    const num = Number(price || 0);
    if (num >= 10000000) return `${Math.round(num / 10000000)} Cr`;
    if (num >= 100000) return `${Math.round(num / 100000)} L`;
    if (num >= 1000) return `${Math.round(num / 1000)} K`;
    return `${num}`;
  };

  // Create marker icon with price label
  const createMarkerIconForProperty = (property, active = false) => {
     try {
       const currentZoom = map ? map.getZoom() : 13;
       const color = active ? '#000000' : '#008000';
       const borderColor = active ? '#FF0000' : '#FFFFFF';
       const showPriceLabel = currentZoom >= 12; // Show price at zoom level 14+
       
       const baseSize = showPriceLabel ? 28 : 16;
       const scale = active ? 1.4 : 1;
       const size = baseSize * scale;
       
       const price = property.price || 0;
       const priceText = getPriceLabel(price);
       
       return L.divIcon({
         className: 'custom-marker',
         html: `
           <div style="
             position: relative;
             width: ${size}px;
             height: ${size}px;
             display: flex;
             align-items: center;
             justify-content: center;
           ">
             <div style="
               background-color: ${color};
               width: 100%;
               height: 100%;
               border-radius: 50%;
               border: 2px solid ${borderColor};
               display: flex;
               justify-content: center;
               align-items: center;
               box-shadow: 0 1px 3px rgba(0,0,0,0.3);
               box-sizing: border-box;
               overflow: hidden;
               position: relative;
               z-index: 2;
             ">
               ${showPriceLabel ? `
                 <span style="
                   color: white;
                   font-size: ${Math.max(8, size * 0.4)}px;
                   font-weight: bold;
                   text-align: center;
                   line-height: 1;
                   padding: 1px;
                   white-space: nowrap;
                   overflow: hidden;
                   text-overflow: ellipsis;
                   max-width: 100%;
                 ">
                   ${priceText}
                 </span>
               ` : ''}
             </div>
           </div>
         `,
         iconSize: [size, size],
         iconAnchor: [size / 2, size / 2],
       });
     } catch (err) {
       console.error('createMarkerIconForProperty error', err);
       return L.divIcon({ className: 'custom-marker' });
     }
   };

  // Update markers when filtered properties change
  useEffect(() => {
    if (!map || !mapLoaded || filteredProperties.length === 0) return;

    try {
      // Clear existing markers
      markers.forEach(m => {
        try {
          if (map.hasLayer(m)) map.removeLayer(m);
        } catch (e) {}
      });

      // Create new markers
      const newMarkers = filteredProperties
        .filter(p => p.lat && p.lng)
        .map(property => {
          const icon = createMarkerIconForProperty(property, String(property.id) === String(activePropertyId));
          
          // FIX: Ensure propertyId is string
          const marker = L.marker([property.lat, property.lng], {
            icon,
            propertyId: String(property.id)
          }).addTo(map);

          // FIX: Updated click handler
          marker.on('click', () => {
            skipMapPanRef.current = true;
            
            // Force string conversion and immediate update
            const propId = String(property.id);
            setActivePropertyId(propId);
            
            // Scroll immediately
            setTimeout(() => {
              scrollToCard(propId);
              skipMapPanRef.current = false;
            }, 10);
          });

          if (String(property.id) === String(activePropertyId)) marker.setZIndexOffset(1000);
          return marker;
        });

      setMarkers(newMarkers);

      // Fit bounds to show all filtered properties
      if (filteredProperties.length > 0 && !searchLocation) {
        const coords = filteredProperties
          .filter(p => p.lat && p.lng)
          .map(p => [p.lat, p.lng]);

        if (coords.length > 0) {
          const bounds = L.latLngBounds(coords);
          map.fitBounds(bounds, {
            padding: [50, 50],
            maxZoom: 5, 
            animate: true
          });
        }
      }

    } catch (error) {
      console.error('Error updating map with filtered properties:', error);
    }
  }, [filteredProperties, map, mapLoaded]);

  // Update marker when active property changes
  useEffect(() => {
    if (!map || !markers.length) return;

    markers.forEach(m => {
      try {
        const pid = String(m.options.propertyId);
        const prop = filteredProperties.find(p => String(p.id) === pid);
        if (prop) {
          const activeId = String(activePropertyId);
          m.setIcon(createMarkerIconForProperty(prop, pid === activeId));

          m.setZIndexOffset(pid === activePropertyId ? 1000 : 0);
        }
      } catch (err) { console.error(err); }
    });

    // Pan to active property if not skipping
    if (!skipMapPanRef.current && map && activePropertyId) {
      try {
        const activeProp = filteredProperties.find(p => String(p.id) === String(activePropertyId));
        if (activeProp && activeProp.lat && activeProp.lng) {
          setTimeout(() => {
            if (map && map.panTo) {
              map.panTo([activeProp.lat, activeProp.lng], { 
                animate: true, 
                duration: 0.4 
              });
            }
          }, 50);
        }
      } catch (error) {
        console.warn('Could not pan to property:', error);
      }
    }
  }, [activePropertyId, markers, map, filteredProperties]);

  // FIX: Scroll to card function - make it more reliable
  const scrollToCard = useCallback((propertyId) => {
    try {
      const container = cardListRef.current;
      if (!container) return;
      
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        const el = container.querySelector(`[data-property-id="${propertyId}"]`);
        if (el) {
          isProgrammaticScrollRef.current = true;
          el.scrollIntoView({ 
            behavior: "smooth", 
            inline: "center", 
            block: "nearest" 
          });
          
          clearTimeout(scrollTimeoutRef.current);
          scrollTimeoutRef.current = setTimeout(() => {
            isProgrammaticScrollRef.current = false;
          }, 450);
        }
      }, 50);
    } catch (err) { 
      console.error('scrollToCard error', err); 
    }
  }, []);

  // Detect scroll to set active property
  useEffect(() => {
    const container = cardListRef.current;
    if (!container) return;

    const onScroll = () => {
      if (isProgrammaticScrollRef.current) return;

      if (scrollDebounceRef.current) clearTimeout(scrollDebounceRef.current);
      scrollDebounceRef.current = setTimeout(() => {
        try {
          const rect = container.getBoundingClientRect();
          const centerX = rect.left + rect.width / 2;
          const cards = Array.from(container.querySelectorAll('[data-property-id]'));
          if (!cards.length) return;

          let closest = null;
          let closestDistance = Infinity;
          cards.forEach(card => {
            const cRect = card.getBoundingClientRect();
            const cardCenter = cRect.left + cRect.width / 2;
            const distance = Math.abs(cardCenter - centerX);
            if (distance < closestDistance) {
              closestDistance = distance;
              closest = card;
            }
          });

          if (closest) {
            const id = closest.getAttribute('data-property-id');
            if (id && id !== lastActiveIdRef.current) {
              // FIX: Force string comparison
              if (String(id) !== String(activePropertyId)) {
                setActivePropertyId(id);
                lastActiveIdRef.current = id;
              }
            }
          }
        } catch (err) {
          console.error('onScroll calc error', err);
        }
      }, 150);
    };

    onScroll();

    container.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    return () => {
      container.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      clearTimeout(scrollTimeoutRef.current);
      clearTimeout(scrollDebounceRef.current);
    };
  }, [filteredProperties, activePropertyId, scrollToCard]);

  // Check carousel scroll position for arrows
  useEffect(() => {
    const container = cardListRef.current;
    if (!container) return;

    const checkScrollPosition = () => {
      if (!container || visibleProperties.length === 0) return;
      
      const scrollLeft = container.scrollLeft;
      const scrollWidth = container.scrollWidth;
      const clientWidth = container.clientWidth;
      
      setIsAtStart(scrollLeft <= 10);
      setIsAtEnd(scrollLeft + clientWidth >= scrollWidth - 10);
    };

    const handleScroll = () => {
      checkScrollPosition();
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    checkScrollPosition();

    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [visibleProperties]);

  // Handle carousel arrow clicks (Infinite Scroll)
  const handleLeftArrowClick = () => {
    const container = cardListRef.current;
    if (!container || visibleProperties.length === 0) return;

    if (isAtStart) {
      container.scrollTo({
        left: container.scrollWidth,
        behavior: 'smooth'
      });
    } else {
      container.scrollBy({ left: -320, behavior: 'smooth' });
    }
  };

  const handleRightArrowClick = () => {
    const container = cardListRef.current;
    if (!container || visibleProperties.length === 0) return;

    if (isAtEnd) {
      container.scrollTo({
        left: 0,
        behavior: 'smooth'
      });
    } else {
      container.scrollBy({ left: 320, behavior: 'smooth' });
    }
  };

  // Handle property type click
  const handlePropertyTypeClick = (type) => {
    setSelectedPropertyType(prev => (prev === type ? null : type));
  };

  // Handle price filter change
  const handlePriceFilterChange = (range) => {
    setPriceFilter(range);
  };

  // Clear all filters
  const clearSearch = () => {
    setSearchLocation(null);
    setSelectedPropertyType(null);
    setPriceFilter(null);
    
    // Remove search location marker
    if (searchLocationMarkerRef.current && map && map.hasLayer(searchLocationMarkerRef.current)) {
      map.removeLayer(searchLocationMarkerRef.current);
      searchLocationMarkerRef.current = null;
    }
    
    setMapKey(prev => prev + 1);
    
    if (cardListRef.current) {
      cardListRef.current.scrollLeft = 0;
    }
    
    setActivePropertyId(null);
  };

  // Handle selected location from search bar
  const handleSelectedLocation = (locationData) => {
    setSearchLocation(locationData);
  };

  useEffect(() => {
    if (filteredProperties.length > 0 && !activePropertyId) {
      const firstPropId = String(filteredProperties[0].id);
      setActivePropertyId(firstPropId);
      
      setTimeout(() => {
        scrollToCard(firstPropId);
      }, 300);
    }
  }, [filteredProperties, activePropertyId, scrollToCard]);

  return (
    <Box
      sx={{
        maxWidth: 480,
        mx: "auto",
        position: "relative",
        backgroundColor: "rgb(239, 231, 221)",
        minHeight: 500,
      }}
    >
      {/* Debug Refresh Button - Can remove after testing */}
      <Button 
        onClick={() => {
          setMapKey(prev => prev + 1);
          if (cardListRef.current) {
            cardListRef.current.scrollLeft = 0;
          }
          if (visibleProperties.length > 0) {
            const firstId = String(visibleProperties[0].id);
            setActivePropertyId(firstId);
            scrollToCard(firstId);
          }
        }}
        sx={{ 
          position: 'absolute', 
          top: 10, 
          right: 10, 
          zIndex: 1000,
          bgcolor: 'white',
          display: 'none' // Hide in production, set to 'block' for debugging
        }}
      >
        Refresh
      </Button>

      {/* Top Section with Search Bar */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 1000,
          bgcolor: "rgb(239, 231, 221)",
          px: 2,
          py: 1,
        }}
      >
        <CustomSearchBar
          onPlaceSelected={handleSelectedLocation}
          onClear={clearSearch}
          onPriceFilterChange={handlePriceFilterChange}
        />
      </Box>

      {/* Title and Filters Section */}
      <Box sx={{ px: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
          Buy Properties
        </Typography>
      </Box>

      <Box sx={{ px: 2 }}>
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            overflowX: 'auto',
            whiteSpace: 'nowrap',
            pb: 1,
            '&::-webkit-scrollbar': {
              display: 'none',
            },
          }}
        >
          {rentalTypes.map((type, index) => (
            <Chip
              key={index}
              label={type}
              variant="filled"
              onClick={() => handlePropertyTypeClick(type)}
              sx={{ 
                flexShrink: 0,
                bgcolor: selectedPropertyType === type ? '#000000' : 'transparent',
                color: selectedPropertyType === type ? '#ffffff' : '#000000',
                border: '1px solid black',
                fontWeight: 'bold',
                '&:hover': {
                  bgcolor: selectedPropertyType === type ? '#000000' : 'transparent',
                  color: selectedPropertyType === type ? '#ffffff' : '#000000',
                },
              }}
            />
          ))}
        </Box>
      </Box>

      <Box sx={{ px: 2, pb: 10, position: "relative" }}>
        {(selectedPropertyType || priceFilter) && filteredProperties.length === 0 && (
          <Box
            sx={{
              position: "absolute",
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 1002,
              width: '100%',
              textAlign: 'center'
            }}
          >
            <Box sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              padding: '20px',
              borderRadius: '12px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              border: '1px solid rgba(0,0,0,0.1)',
            }}>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
                No properties found
              </Typography>
              
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {selectedPropertyType && `Category: "${selectedPropertyType}"`}
                {selectedPropertyType && priceFilter && ' • '}
                {priceFilter && `Price: ${priceFilter.label}`}
              </Typography>
              
              <Button 
                variant="outlined" 
                sx={{ mt: 2 }}
                onClick={() => {
                  setSelectedPropertyType(null);
                  setPriceFilter(null);
                }}
              >
                Clear Filters
              </Button>
            </Box>
          </Box>
        )}

        <div
          ref={mapContainerRef}
          key={`map-${mapKey}`}
          style={{
            width: "100vw",
            height: "calc(111vh - 300px)",
            marginLeft: "-16px",
            marginRight: "-16px",
            borderRadius: "6px",
            overflow: "hidden",
            background: "#eaeaea",
          }}
        >
          {!mapLoaded && (
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%'
            }}>
              <Typography>Loading map...</Typography>
            </Box>
          )}
        </div>

        {visibleProperties.length > 0 && (
          <Box
            sx={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: 78,
              zIndex: 1001,
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              pointerEvents: 'auto',
            }}
          >
            <Button
              onClick={handleLeftArrowClick}
              sx={{
                minWidth: 0,
                width: 32,
                height: 32,
                p: 1,
                left: 5,
                borderRadius: '70%',
                bgcolor: 'white',
                boxShadow: 2,
                mr: 1,
                '&:hover': { bgcolor: 'grey.200' },
                zIndex: 1002,
              }}
            >
              &lt;
            </Button>

            <Box
              sx={{
                display: 'flex',
                overflowX: 'auto',
                gap: 2,
                flexGrow: 1,
                scrollSnapType: 'x mandatory',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
              }}
              ref={cardListRef}
            >
              {visibleProperties.map((prop) => (
                <Box
                  key={prop.id}
                  data-property-id={prop.id}
                  sx={{
                    flex: '0 0 280px',
                    scrollSnapAlign: 'center',
                    cursor: 'pointer',
                    height: '130px',
                  }}
                  onClick={(e) => 
                    requireLogin(e, () => {
                      // FIX: Ensure string ID
                      const propId = String(prop.id);
                      setActivePropertyId(propId);
                      navigate(`/Buy-description/${propId}`);
                    })
                  }
                >
                  <div style={{ padding: 0, height: '100%' }}>
                    <ReUsableCard
                      property={prop}
                      onCardClick={() => {
                        // FIX: Ensure string ID
                        const propId = String(prop.id);
                        setActivePropertyId(propId);
                        navigate(`/Buy-description/${propId}`);
                      }}
                      isSaved={isSaved}
                      toggleSave={toggleSave}
                      likedCards={likedCards}
                      toggleLike={toggleLike}
                      saving={saving[prop.id]}
                      isSubscribed={isSubscribed}
                      userType="Buyer"
                    />
                  </div>
                </Box>
              ))}
            </Box>

            {/* Right Arrow */}
            <Button
              onClick={handleRightArrowClick}
              sx={{
                minWidth: 0,
                width: 32,
                height: 32,
                borderRadius: '50%',
                bgcolor: 'white',
                boxShadow: 2,
                p: 1,
                right: 5,
                ml: 1,
                '&:hover': { bgcolor: 'grey.200' },
                zIndex: 1002,
              }}
            >
              &gt;
            </Button>
          </Box>
        )}

        {/* No Properties in View Message */}
        {visibleProperties.length === 0 && filteredProperties.length > 0 && (
          <Box sx={{
            position: "absolute",
            bottom: 10,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1001,
            backgroundColor: "rgba(255,255,255,0.9)",
            padding: "8px 16px",
            borderRadius: "4px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
          }}>
            <Typography variant="body2">
              No properties in current view. Zoom or pan to see properties.
            </Typography>
          </Box>
        )}
      </Box>

      <GuestLoginModal
        open={showGuestModal}
        onClose={() => {
          setShowGuestModal(false);
        }}
      />

      {/* Bottom Navigation */}
      <Paper
        sx={{ position: "fixed", bottom: 0, left: 0, right: 0 }}
        elevation={3}
      >
        <CustomNav />
      </Paper>
    </Box>
  );
};

export default PostBankMap;